// ============================================================================
// ADD-ON REQUEST HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { addonRequestService } from 'api/services/addonRequestService';
import type { AddonRequest, CreateAddonRequestPayload, UpdateAddonRequestPayload } from 'api/types/addonRequest.types';
import type { ID } from 'api/types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'addon-requests';

export const useAddonRequests = () => {
  const [data, setData] = useState<AddonRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await addonRequestService.getAll({ pageSize: 50000 });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useDataChangeListener(DATA_KEY, fetchRequests);

  return { data, loading, error, refetch: fetchRequests };
};

export const useCreateAddonRequest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createRequest = async (data: CreateAddonRequestPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await addonRequestService.create(data);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { createRequest, loading, error };
};

export const useUpdateAddonRequest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateRequest = async (id: ID, data: UpdateAddonRequestPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await addonRequestService.update(id, data);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { updateRequest, loading, error };
};

export const useDeleteAddonRequest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteRequest = async (id: ID) => {
    setLoading(true);
    setError(null);
    try {
      await addonRequestService.delete(id);
      notifyDataChanged(DATA_KEY);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { deleteRequest, loading, error };
};

export const useSubmitAddonRequest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submitRequest = async (id: ID) => {
    setLoading(true);
    setError(null);
    try {
      const response = await addonRequestService.submit(id);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { submitRequest, loading, error };
};
